/**
 * Comptabilité Service - API calls for Accounting module
 * Handles: Chart of Accounts, Journal Entries, Financial Statements, Tax Declarations
 */

import {
  getComplianceOverview,
  getComplianceCheckCategories,
  getComplianceAuditLog,
  getComplianceBackupStatus,
  getComplianceChecks,
  updateComplianceCheck,
  transformComplianceCheck,
  getEventLog,
  getEventLogStats,
  logEvent,
  getVersions,
  addVersion,
  getParameterHistory,
  getParameterComparison,
  getBackupLog,
  logBackup,
} from './comptabilite/compliance';
import {
  getLiasseFiscale,
  createLiasseFiscaleItem,
  updateLiasseFiscaleItem,
  saveManualTableau,
  generateLiasseFiscale,
  calculateAllTableaux,
  calculateSpecificTableau,
  exportLiasse,
} from '../../pages/comptabilite/15-liasse-fiscale/liasse-fiscale';
import {
  getIfrsMapping,
  getIfrsStandards,
  getIfrsDifferences,
  createIfrsMapping,
} from './comptabilite/ifrs-support';
import {
  getSpecializedSectorTypes,
  getSpecializedSectorData,
} from '../../pages/comptabilite/19-secteurs-specialises/specialized-sectors';
import { demoHttpApiCall } from '../../online-demo/services/demoHttpApiBridge';
import { isCloudDemoRuntime } from './publicDemoRuntime';
import { API_BASE } from './apiConfig';
import { normalizeCurrencyVolume } from './comptabiliteConstants';
import i18n from '../../i18n';

const exportT = (key, opts) =>
  i18n.t(`modules.comptabilite.pages.accountingExportUtils.${key}`, opts);

const IS_CLOUD_DEMO_BROWSER =
  isCloudDemoRuntime() &&
  typeof window !== 'undefined' &&
  !(typeof window !== 'undefined' && window.electronAPI && typeof window.electronAPI.get === 'function');

/** Normalize demo bridge responses to match backend `{ data: ... }` shape. */
const normalizeDemoApiResult = (result) => {
  if (Array.isArray(result)) return { data: result };
  if (
    result &&
    typeof result === 'object' &&
    result.data === undefined &&
    result.success === undefined &&
    result.error === undefined &&
    result.demo === undefined &&
    result.items === undefined
  ) {
    return { data: result };
  }
  return result;
};

/** Map canonical /comptabilite paths to legacy /accounting when backend not restarted yet */
const toLegacyAccountingEndpoint = (endpoint) => {
  const exact = {
    '/comptabilite/bilan': '/accounting/balance-sheet',
    '/comptabilite/resultat': '/situation/resultat',
    '/comptabilite/grand-livre': '/situation/grand-livre',
    '/comptabilite/journal': '/situation/journal',
    '/comptabilite/flux': '/situation/flux',
    '/comptabilite/balance/preview/generate': '/accounting/balance-preview/generate',
    '/comptabilite/analyses-rapports': '/accounting/monthly-results',
  };
  if (exact[endpoint]) return exact[endpoint];

  if (endpoint.startsWith('/comptabilite/grand-livre/')) {
    return endpoint.replace('/comptabilite/grand-livre', '/situation/grand-livre');
  }
  if (endpoint.startsWith('/comptabilite/bilan/')) {
    return endpoint.replace('/comptabilite/bilan', '/accounting/balance-sheet');
  }
  if (endpoint.startsWith('/comptabilite/resultat/')) {
    return endpoint.replace('/comptabilite/resultat', '/situation/resultat');
  }
  if (endpoint.startsWith('/comptabilite/flux')) {
    return endpoint.replace('/comptabilite/flux', '/situation/flux');
  }
  if (endpoint.startsWith('/comptabilite/annexes')) {
    return endpoint.replace('/comptabilite/annexes', '/situation/annexes');
  }
  if (endpoint.startsWith('/comptabilite/rapports')) {
    return endpoint.replace('/comptabilite/rapports', '/situation/rapports');
  }
  if (endpoint.startsWith('/comptabilite/assets')) {
    return endpoint.replace('/comptabilite/assets', '/accounting/assets');
  }
  if (endpoint.startsWith('/comptabilite/journal/authorization')) {
    return endpoint.replace('/comptabilite/journal/authorization', '/accounting/journal-authorization');
  }
  if (endpoint.startsWith('/comptabilite/journal/journals')) {
    return endpoint.replace('/comptabilite/journal/journals', '/accounting/journals');
  }
  if (endpoint.startsWith('/comptabilite/journal/')) {
    return endpoint.replace('/comptabilite/journal', '/situation/journal');
  }
  if (endpoint.startsWith('/comptabilite/config/')) {
    return endpoint.replace('/comptabilite/config/', '/accounting/');
  }
  if (endpoint.startsWith('/comptabilite/tax-declarations/tvcp')) {
    return endpoint.replace('/comptabilite/tax-declarations/tvcp', '/accounting/tvcp');
  }
  if (endpoint.startsWith('/comptabilite/tax-declarations/tvcp')) {
    return endpoint.replace('/comptabilite/tax-declarations/tvcp', '/accounting/tvcp');
  }
  if (endpoint.startsWith('/comptabilite/comparatifs')) {
    return endpoint.replace('/comptabilite/comparatifs', '/situation/comparatifs');
  }
  if (endpoint.startsWith('/comptabilite/cloture')) {
    return endpoint.replace('/comptabilite/cloture', '/situation/cloture');
  }
  if (endpoint.startsWith('/comptabilite/analyses-rapports/')) {
    return endpoint.replace('/comptabilite/analyses-rapports', '/accounting/monthly-results');
  }
  if (endpoint.startsWith('/comptabilite/balance')) {
    return endpoint.replace('/comptabilite/balance', '/situation/balance');
  }
  if (endpoint.startsWith('/comptabilite/')) {
    return endpoint.replace('/comptabilite/', '/accounting/');
  }
  return null;
};

const formatApiError = (result, status) => {
  const err = result?.error;
  if (typeof err === 'string') return err;
  if (err?.message) return err.message;
  return i18n.t('serviceMessages.common.http_error', { status });
};

const apiCallOnce = async (endpoint, method = 'GET', data = null) => {
  if (IS_CLOUD_DEMO_BROWSER) {
    const raw = await demoHttpApiCall(endpoint, method, data);
    return normalizeDemoApiResult(raw);
  }

  const options = {
    method,
    headers: { 'Content-Type': 'application/json' },
  };
  if (data) options.body = JSON.stringify(data);

  const response = await fetch(`${API_BASE}${endpoint}`, options);
  const result = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(formatApiError(result, response.status));
    error.httpStatus = response.status;
    throw error;
  }
  return result;
};

const apiCall = async (endpoint, method = 'GET', data = null) => {
  try {
    return await apiCallOnce(endpoint, method, data);
  } catch (error) {
    const isNetwork =
      error?.name === 'TypeError'
      || String(error?.message || '').includes('Failed to fetch');
    if (isNetwork && method === 'GET') {
      console.warn(`Comptabilité API GET ${endpoint}: backend unreachable`, error);
      return { data: [] };
    }
    if (error.httpStatus === 404 && endpoint.startsWith('/comptabilite/')) {
      const legacyEndpoint = toLegacyAccountingEndpoint(endpoint);
      if (legacyEndpoint && legacyEndpoint !== endpoint) {
        try {
          return await apiCallOnce(legacyEndpoint, method, data);
        } catch (legacyError) {
          console.error(`API Error (${method} ${legacyEndpoint} legacy fallback):`, legacyError);
          throw legacyError;
        }
      }
    }
    console.error(`API Error (${method} ${endpoint}):`, error);
    throw error;
  }
};

// =====================================================
// DASHBOARD STATS
// =====================================================
export const getDashboardStats = async () => ({});

export const getDashboardKPIs = async () => ({});

// =====================================================
// CHART OF ACCOUNTS (Plan Comptable)
// =====================================================
const ACCOUNT_CLASS_TYPE = {
  '1': 'equity',
  '2': 'asset',
  '3': 'asset',
  '4': 'mixed',
  '5': 'asset',
  '6': 'expense',
  '7': 'revenue',
};

const mapAccountFormToPayload = (data) => {
  const code = String(data.code || data.account_number || '').trim();
  const name = String(data.name || data.account_name || '').trim();
  const classFromForm = String(data.class || data.class_number || '').trim();
  const classNumber = classFromForm || code.charAt(0) || '';
  const uiType = data.type || 'detail';
  const level = uiType === 'class' ? 1 : uiType === 'group' ? 2 : 3;
  return {
    account_number: code,
    account_name: name,
    account_type: data.account_type || ACCOUNT_CLASS_TYPE[classNumber] || 'asset',
    class_number: classNumber,
    parent_account: data.parent_account || data.parentCode || null,
    level,
    balance_type: data.debitNature ? 'debit' : 'credit',
    opening_balance: parseFloat(data.balance ?? data.opening_balance) || 0,
    is_active: data.isActive ? 1 : 0,
  };
};

export const getChartOfAccounts = async (filters = {}) => {
  const queryParams = new URLSearchParams(filters).toString();
  const endpoint = `/comptabilite/accounts${queryParams ? `?${queryParams}` : ''}`;
  const result = await apiCall(endpoint);
  return result.data || [];
};

export const getAccount = async (id) => {
  const result = await apiCall(`/comptabilite/accounts/${id}`);
  return result.data;
};

export const createAccount = async (data) => {
  const payload = mapAccountFormToPayload(data);
  const result = await apiCall('/comptabilite/accounts', 'POST', payload);
  return result.data;
};

export const updateAccount = async (id, data) => {
  const payload = mapAccountFormToPayload(data);
  const result = await apiCall(`/comptabilite/accounts/${id}`, 'PUT', {
    account_name: payload.account_name,
    account_type: payload.account_type,
    class_number: payload.class_number,
    level: payload.level,
    balance_type: payload.balance_type,
    is_active: payload.is_active,
  });
  return result.data;
};

export const deleteAccount = async (id) => {
  const result = await apiCall(`/comptabilite/accounts/${id}`, 'DELETE');
  return result.data;
};

export const getAnalyticalCodes = async (type) => {
  const result = await apiCall(`/comptabilite/analytical-codes/${type}`);
  return result.data || [];
};

export const createAnalyticalCode = async (type, data) => {
  const result = await apiCall(`/comptabilite/analytical-codes/${type}`, 'POST', data);
  return result.data;
};

export const updateAnalyticalCode = async (type, id, data) => {
  const result = await apiCall(`/comptabilite/analytical-codes/${type}/${id}`, 'PUT', data);
  return result.data;
};

export const deleteAnalyticalCode = async (type, id) => {
  const result = await apiCall(`/comptabilite/analytical-codes/${type}/${id}`, 'DELETE');
  return result.data;
};

// =====================================================
// JOURNAL ENTRIES (Écritures Comptables)
// =====================================================
export const getJournalEntries = async () => {
  const result = await apiCall('/comptabilite/entries');
  return result.data || [];
};

export const getJournalEntry = async (id) => {
  const result = await apiCall(`/comptabilite/entries/${id}`);
  return result.data;
};

const buildJournalEntryPayload = async (data) => {
  const entryDate = data.date || data.entry_date || data.entryDate || '';
  if (!entryDate) {
    throw new Error(exportT('error_entry_date_required'));
  }

  let fiscalYearId = data.fiscal_year_id;
  if (!fiscalYearId) {
    const currentYear = await getCurrentFiscalYear();
    fiscalYearId = currentYear?.id;
  }
  if (!fiscalYearId) {
    throw new Error(exportT('error_no_current_fiscal_year'));
  }

  const headerDateDocument = data.date_document || '';
  const headerDateEcheance = data.date_echeance || '';

  const lines = (data.lines || []).map((line) => ({
    account: line.account || line.account_number,
    account_number: line.account_number || line.account,
    account_name: line.label || line.account_name || line.description,
    label: line.label,
    description: line.label || line.description,
    debit: parseFloat(line.debit) || 0,
    credit: parseFloat(line.credit) || 0,
    date_document: line.date_document || headerDateDocument,
    date_echeance: line.date_echeance || headerDateEcheance,
    lettrage: line.lettrage || line.lettering_code || '',
    auxiliary_code: line.auxiliary_code || '',
    poste_code: line.poste_code || '',
    nature_code: line.nature_code || '',
    unite_code: line.unite_code || '',
    is_deductible: line.is_deductible,
    is_fiscal_dotation: line.is_fiscal_dotation,
    is_wholesale: line.is_wholesale,
    provision_type: line.provision_type || '',
    transfer_type: line.transfer_type || '',
  }));

  const period = entryDate.substring(0, 7);
  const entryNumber = data.piece_number || data.entry_number || `OD-${entryDate.replace(/-/g, '')}-${Date.now().toString().slice(-4)}`;

  return {
    entry_number: entryNumber,
    entry_date: entryDate,
    reference: data.reference || '',
    description: data.description || '',
    folio: data.folio || '',
    date_document: data.date_document || '',
    date_echeance: data.date_echeance || '',
    piece_number: data.piece_number || entryNumber,
    journal_id: data.journal_id || 'j_od',
    journal_type: data.journal_type || 'OD',
    fiscal_year_id: fiscalYearId,
    period,
    status: data.status || 'brouillon',
    lines,
  };
};

export const createJournalEntry = async (data) => {
  const payload = await buildJournalEntryPayload(data);
  const result = await apiCall('/comptabilite/entries', 'POST', payload);
  return result.data;
};

export const updateJournalEntry = async (id, data) => {
  const payload = await buildJournalEntryPayload(data);
  const result = await apiCall(`/comptabilite/entries/${id}`, 'PUT', payload);
  return result.data;
};

export const deleteJournalEntry = async (id) => {
  const result = await apiCall(`/comptabilite/entries/${id}`, 'DELETE');
  return result.data;
};

export const validateJournalEntry = async (id, validatedBy) => {
  const now = new Date().toISOString();
  return updateJournalEntry(id, { status: 'validated', validated_by: validatedBy, validated_at: now });
};

// =====================================================
// BALANCE SHEET (Bilan Comptable)
// =====================================================
export const getBalanceSheet = async (fiscalYear = null) => {
  let endpoint = '/comptabilite/bilan';
  if (fiscalYear) endpoint += `?fiscal_year=${fiscalYear}`;
  const result = await apiCall(endpoint);
  return result.data || [];
};

export const createBalanceSheetItem = async (data) => {
  const now = new Date().toISOString();
  const payload = { ...data, created_at: now, updated_at: now };
  const result = await apiCall('/comptabilite/bilan', 'POST', payload);
  return result.data;
};

export const updateBalanceSheetItem = async (id, data) => {
  const payload = { ...data, updated_at: new Date().toISOString() };
  const result = await apiCall(`/comptabilite/bilan/${id}`, 'PUT', payload);
  return result.data;
};

// =====================================================
// INCOME STATEMENT (Compte de Résultat)
// =====================================================
export const getIncomeStatement = async (fiscalYear = null, type = null) => {
  let endpoint = '/comptabilite/resultat';
  const params = [];
  if (fiscalYear) params.push(`fiscal_year=${fiscalYear}`);
  if (type) params.push(`statement_type=${type}`);
  if (params.length) endpoint += `?${params.join('&')}`;
  const result = await apiCall(endpoint);
  // Ensure proper structure for income statement data
  const data = result.data || {};
  if (Array.isArray(data)) {
    return {
      byNature: { title: exportT('fallback_income_by_nature'), sections: [] },
      byFunction: { title: exportT('fallback_income_by_function'), sections: [] }
    };
  }
  return data;
};

export const createIncomeStatementItem = async (data) => {
  const now = new Date().toISOString();
  const payload = { ...data, created_at: now, updated_at: now };
  const result = await apiCall('/comptabilite/resultat', 'POST', payload);
  return result.data;
};

// =====================================================
// CASH FLOW STATEMENT (Flux de Trésorerie)
// =====================================================
const unwrapSituationList = (result) => (Array.isArray(result) ? result : result?.data || []);

export const getCashFlowStatement = async (fiscalYear = null) => {
  let endpoint = '/comptabilite/flux';
  if (fiscalYear) endpoint += `?fiscal_year=${fiscalYear}`;
  const result = await apiCall(endpoint);
  return unwrapSituationList(result);
};

export const createCashFlowItem = async (data) => {
  const now = new Date().toISOString();
  const payload = { ...data, created_at: now, updated_at: now };
  return apiCall('/comptabilite/flux', 'POST', payload);
};

// A08 — Flux de Trésorerie (Situation)
export const getFluxTresorerie = async () => {
  const result = await apiCall('/comptabilite/flux');
  return unwrapSituationList(result);
};

export const createFluxTresorerie = async (payload) => {
  return apiCall('/comptabilite/flux', 'POST', payload);
};

export const updateFluxTresorerie = async (id, payload) => {
  return apiCall(`/comptabilite/flux/${id}`, 'PUT', payload);
};

export const deleteFluxTresorerie = async (id) => {
  return apiCall(`/comptabilite/flux/${id}`, 'DELETE');
};

// =====================================================
// GRAND LIVRE (A04)
// =====================================================
export const getGrandLivreEntries = async () => {
  const result = await apiCall('/comptabilite/grand-livre');
  return unwrapSituationList(result);
};

export const createGrandLivreEntry = async (payload) => {
  return apiCall('/comptabilite/grand-livre', 'POST', payload);
};

export const updateGrandLivreEntry = async (id, payload) => {
  return apiCall(`/comptabilite/grand-livre/${id}`, 'PUT', payload);
};

export const deleteGrandLivreEntry = async (id) => {
  return apiCall(`/comptabilite/grand-livre/${id}`, 'DELETE');
};

// =====================================================
// JOURNAL COMPTABLE SITUATION (A05)
// =====================================================
export const getJournalComptableEntries = async () => {
  const result = await apiCall('/comptabilite/journal');
  return unwrapSituationList(result);
};

export const createJournalComptableEntry = async (payload) => {
  return apiCall('/comptabilite/journal', 'POST', payload);
};

export const updateJournalComptableEntry = async (id, payload) => {
  return apiCall(`/comptabilite/journal/${id}`, 'PUT', payload);
};

export const deleteJournalComptableEntry = async (id) => {
  return apiCall(`/comptabilite/journal/${id}`, 'DELETE');
};

// =====================================================
// BILAN SITUATION CRUD (A06)
// =====================================================
export const getBilans = async () => {
  const result = await apiCall('/comptabilite/bilan');
  return unwrapSituationList(result);
};

export const computeBilanFromEntries = async (fiscalYearId = null) => {
  const qs = fiscalYearId ? `?fiscal_year_id=${encodeURIComponent(fiscalYearId)}` : '';
  const result = await apiCall(`/comptabilite/bilan/compute/from-entries${qs}`);
  return result?.data ?? result;
};

export const createBilan = async (payload) => {
  return apiCall('/comptabilite/bilan', 'POST', payload);
};

export const updateBilan = async (id, payload) => {
  return apiCall(`/comptabilite/bilan/${id}`, 'PUT', payload);
};

export const deleteBilan = async (id) => {
  return apiCall(`/comptabilite/bilan/${id}`, 'DELETE');
};

// =====================================================
// COMPTE RÉSULTAT SITUATION CRUD (A07)
// =====================================================
export const getResultats = async () => {
  const result = await apiCall('/comptabilite/resultat');
  return unwrapSituationList(result);
};

export const createResultat = async (payload) => {
  return apiCall('/comptabilite/resultat', 'POST', payload);
};

export const updateResultat = async (id, payload) => {
  return apiCall(`/comptabilite/resultat/${id}`, 'PUT', payload);
};

export const deleteResultat = async (id) => {
  return apiCall(`/comptabilite/resultat/${id}`, 'DELETE');
};

// =====================================================
// TAX DECLARATIONS (Déclarations Fiscales)
// =====================================================
export const getTaxDeclarations = async () => {
  const result = await apiCall('/comptabilite/tax-declarations');
  // Ensure proper structure for tax declarations data
  const data = result.data || {};
  if (Array.isArray(data)) {
    return {
      vat_return: { title: 'إقرار ضريبة القيمة المضافة', sections: [] },
      zakat: { title: 'إقرار الزكاة', sections: [] },
    };
  }
  return data;
};

export const listTaxDeclarationRecords = async () => {
  const result = await apiCall('/comptabilite/tax-declarations');
  const data = result?.data;
  return Array.isArray(data) ? data : [];
};

export const getTaxDeclaration = async (id) => {
  const result = await apiCall(`/comptabilite/tax-declarations/${id}`);
  return result.data;
};

export const createTaxDeclaration = async (data) => {
  const now = new Date().toISOString();
  const payload = {
    declaration_number: data.declaration_number || data.code || data.declarationNumber,
    declaration_type: data.declarationType || data.type,
    declaration_title: data.title,
    declaration_title_ar: data.titleAr || '',
    description: data.description || '',
    description_ar: data.descriptionAr || '',
    period: data.period,
    fiscal_year: data.fiscalYear,
    due_date: data.dueDate,
    status: data.status || 'draft',
    total_base: data.totalBase || 0,
    total_tax: data.totalTax || 0,
    penalties: data.penalties || 0,
    net_amount: data.netAmount || 0,
    notes: data.notes || '',
    created_at: now,
    updated_at: now,
  };
  const result = await apiCall('/comptabilite/tax-declarations', 'POST', payload);
  return result.data;
};

export const updateTaxDeclaration = async (id, data) => {
  const payload = { ...data, updated_at: new Date().toISOString() };
  const result = await apiCall(`/comptabilite/tax-declarations/${id}`, 'PUT', payload);
  return result.data;
};

export const deleteTaxDeclaration = async (id) => {
  const result = await apiCall(`/comptabilite/tax-declarations/${id}`, 'DELETE');
  return result.data;
};

// =====================================================
// TAX DECLARATION ITEMS
// =====================================================
export const getTaxDeclarationItems = async (declarationId) => {
  const result = await apiCall(`/comptabilite/tax-declarations/items?declaration_id=${declarationId}`);
  return result.data || [];
};

export const createTaxDeclarationItem = async (data) => {
  const now = new Date().toISOString();
  const payload = { ...data, created_at: now, updated_at: now };
  const result = await apiCall('/comptabilite/tax-declarations/items', 'POST', payload);
  return result.data;
};

// =====================================================
// TAX CALENDAR (Calendrier Fiscal)
// =====================================================
export const getTaxCalendar = async () => {
  const result = await apiCall('/comptabilite/tax-declarations/calendar');
  return result.data || [];
};

export const createTaxCalendarEntry = async (data) => {
  const now = new Date().toISOString();
  const payload = { ...data, created_at: now, updated_at: now };
  const result = await apiCall('/comptabilite/tax-declarations/calendar', 'POST', payload);
  return result.data;
};

export const updateTaxCalendarEntry = async (id, data) => {
  const payload = { ...data, updated_at: new Date().toISOString() };
  const result = await apiCall(`/comptabilite/tax-declarations/calendar/${id}`, 'PUT', payload);
  return result.data;
};

// =====================================================
// COMPLIANCE CHECKS (Conformité & Audit) — see comptabilite/compliance/
// =====================================================
export {
  getComplianceOverview,
  getComplianceCheckCategories,
  getComplianceAuditLog,
  getComplianceBackupStatus,
  getComplianceChecks,
  updateComplianceCheck,
  transformComplianceCheck,
  getEventLog,
  getEventLogStats,
  logEvent,
  getVersions,
  addVersion,
  getParameterHistory,
  getParameterComparison,
  getBackupLog,
  logBackup,
};

// =====================================================
// ANNEXE NOTES (Annexes aux États Financiers)
// =====================================================
export const getAnnexeNotes = async (fiscalYear = null) => {
  let endpoint = '/comptabilite/annexes';
  if (fiscalYear) endpoint += `?fiscal_year=${fiscalYear}`;
  const result = await apiCall(endpoint);
  return unwrapSituationList(result);
};

export const createAnnexeNote = async (data) => {
  const now = new Date().toISOString();
  const payload = { ...data, created_at: now, updated_at: now };
  return apiCall('/comptabilite/annexes', 'POST', payload);
};

export const updateAnnexeNote = async (id, data) => {
  const payload = { ...data, updated_at: new Date().toISOString() };
  return apiCall(`/comptabilite/annexes/${id}`, 'PUT', payload);
};

export const deleteAnnexeNote = async (id) => {
  return apiCall(`/comptabilite/annexes/${id}`, 'DELETE');
};

// A11 — Rapports Périodiques
export const getRapportsPeriodiques = async () => {
  const result = await apiCall('/comptabilite/rapports');
  return unwrapSituationList(result);
};

export const createRapportPeriodique = async (payload) => {
  return apiCall('/comptabilite/rapports', 'POST', payload);
};

export const updateRapportPeriodique = async (id, payload) => {
  return apiCall(`/comptabilite/rapports/${id}`, 'PUT', payload);
};

export const deleteRapportPeriodique = async (id) => {
  return apiCall(`/comptabilite/rapports/${id}`, 'DELETE');
};

// =====================================================
// LIASSE FISCALE (Tax Package) — see 15-liasse-fiscale/
// =====================================================
export {
  getLiasseFiscale,
  createLiasseFiscaleItem,
  updateLiasseFiscaleItem,
  saveManualTableau,
  generateLiasseFiscale,
  calculateAllTableaux,
  calculateSpecificTableau,
  exportLiasse,
};

// =====================================================
// MONTHLY TRACKING (Analyses & Rapports)
// =====================================================
export const getMonthlyTracking = async (fiscalYearId = null) => {
  let endpoint = '/comptabilite/analyses-rapports';
  if (fiscalYearId) endpoint += `?fiscal_year_id=${fiscalYearId}`;
  const result = await apiCall(endpoint);
  const raw = result?.data ?? result;

  if (Array.isArray(raw)) {
    const months = raw.map((row) => ({
      month: row.month,
      chiffreAffaires: Number(row.revenue ?? row.chiffre_affaires ?? 0),
      charges: Number(row.expenses ?? row.charges ?? 0),
      resultat: Number(row.profit ?? row.resultat ?? 0),
    }));

    const totalCA = months.reduce((sum, row) => sum + row.chiffreAffaires, 0);
    const totalCharges = months.reduce((sum, row) => sum + row.charges, 0);
    const totalResultat = months.reduce((sum, row) => sum + row.resultat, 0);
    const growthValues = months.slice(1).map((row, idx) => {
      const prev = months[idx]?.chiffreAffaires || 0;
      if (!prev) return 0;
      return ((row.chiffreAffaires - prev) / prev) * 100;
    });
    const avgGrowth = growthValues.length
      ? Math.round((growthValues.reduce((a, b) => a + b, 0) / growthValues.length) * 10) / 10
      : 0;

    return {
      overview: {
        currentPeriod: months[months.length - 1]?.month?.slice(0, 4) || '2026',
        totalMonths: months.length,
        lastUpdate: months[months.length - 1]?.month || '—',
        avgGrowth,
        forecast2025: Math.round(totalResultat * 1.1),
        completedMonths: months.length,
      },
      periods: ['1an', '2ans', '4ans'],
      data: {
        '1an': {
          months,
          kpis: {
            totalCA,
            totalCharges,
            totalResultat,
            croissanceCA: avgGrowth,
          },
        },
        '2ans': { quarters: [], kpis: {} },
        '4ans': { years: [], kpis: {} },
      },
    };
  }

  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    return raw;
  }

  return {
    overview: {
      currentPeriod: '2026',
      totalMonths: 0,
      lastUpdate: '—',
      avgGrowth: 0,
      forecast2025: 0,
      completedMonths: 0,
    },
    periods: ['1an', '2ans', '4ans'],
    data: {
      '1an': { months: [], kpis: {} },
      '2ans': { quarters: [], kpis: {} },
      '4ans': { years: [], kpis: {} },
    },
  };
};

export const createMonthlyTrackingEntry = async (data) => {
  const now = new Date().toISOString();
  const payload = { ...data, created_at: now, updated_at: now };
  const result = await apiCall('/comptabilite/analyses-rapports/forecast', 'POST', payload);
  return result.data;
};

export const updateMonthlyTrackingEntry = async (id, data) => {
  const payload = { ...data, updated_at: new Date().toISOString() };
  const result = await apiCall(`/comptabilite/analyses-rapports/${id}`, 'PUT', payload);
  return result.data;
};

// =====================================================
// ACCOUNTING STATS (Statistiques Comptables)
// =====================================================
export const getAccountingStats = async (fiscalYear = null) => {
  let endpoint = '/accounting_stats';
  if (fiscalYear) endpoint += `?fiscal_year=${fiscalYear}`;
  const result = await apiCall(endpoint);
  return result.data || [];
};

export const updateAccountingStats = async (id, data) => {
  const payload = { ...data, computed_at: new Date().toISOString(), updated_at: new Date().toISOString() };
  const result = await apiCall(`/accounting_stats/${id}`, 'PUT', payload);
  return result.data;
};

// =====================================================
// IFRS MAPPING — see comptabilite/ifrs-support/
// =====================================================
export {
  getIfrsMapping,
  getIfrsStandards,
  getIfrsDifferences,
  createIfrsMapping,
};

// =====================================================
// SPECIALIZED SECTORS (A19) — see 19-secteurs-specialises/
// =====================================================
export {
  getSpecializedSectorTypes,
  getSpecializedSectorData,
};

// =====================================================
// ADDITIONAL TAXES (IRA, VF, TAP, Foncier…)
// =====================================================
export const getAdditionalTaxes = async (filters = {}) => {
  let endpoint = '/comptabilite/additional-taxes';
  const params = [];
  if (filters.tax_code) params.push(`tax_code=${filters.tax_code}`);
  if (filters.status) params.push(`status=${filters.status}`);
  if (filters.period) params.push(`period=${filters.period}`);
  if (params.length) endpoint += `?${params.join('&')}`;
  const result = await apiCall(endpoint);
  return result.data || [];
};

export const calculateAdditionalTax = async (tax_code, base_amount, period) => {
  const result = await apiCall('/comptabilite/additional-taxes/calculate', 'POST', {
    tax_code, base_amount, period,
  });
  return result.data;
};

export const recordAdditionalTax = async (data) => {
  const result = await apiCall('/comptabilite/additional-taxes', 'POST', data);
  return result.data;
};

// =====================================================
// ASSETS (Immobilisations)
// =====================================================
export const getAssets = async (filters = {}) => {
  let endpoint = '/comptabilite/assets';
  const params = [];
  if (filters.category) params.push(`category=${filters.category}`);
  if (filters.status) params.push(`status=${filters.status}`);
  if (filters.search) params.push(`search=${encodeURIComponent(filters.search)}`);
  if (params.length) endpoint += `?${params.join('&')}`;
  
  const result = await apiCall(endpoint);
  return result.data || [];
};

export const getAsset = async (id) => {
  const result = await apiCall(`/comptabilite/assets/${id}`);
  return result.data;
};

export const createAsset = async (data) => {
  const now = new Date().toISOString();
  const payload = {
    asset_number: data.assetNumber || `ASSET-${Date.now()}`,
    name: data.name,
    description: data.description || '',
    category: data.category,
    purchase_date: data.purchaseDate,
    purchase_price: data.purchasePrice,
    current_value: data.currentValue || data.purchasePrice,
    depreciation_rate: data.depreciationRate || 0,
    depreciation_method: data.depreciationMethod || 'linear',
    useful_life_years: data.usefulLifeYears || 5,
    accumulated_depreciation: data.accumulatedDepreciation || 0,
    location: data.location || '',
    supplier: data.supplier || '',
    serial_number: data.serialNumber || '',
    status: data.status || 'active',
    notes: data.notes || '',
    account_number: data.accountNumber || '2',
    created_at: now,
    updated_at: now
  };
  const result = await apiCall('/comptabilite/assets', 'POST', payload);
  return result.data;
};

export const updateAsset = async (id, data) => {
  const payload = { ...data, updated_at: new Date().toISOString() };
  const result = await apiCall(`/comptabilite/assets/${id}`, 'PUT', payload);
  return result.data;
};

export const deleteAsset = async (id) => {
  const result = await apiCall(`/comptabilite/assets/${id}`, 'DELETE');
  return result.data;
};

// =====================================================
// AUXILIARIES (Comptes Auxiliaires)
// =====================================================
export const getAuxiliaries = async (filters = {}) => {
  let endpoint = '/comptabilite/auxiliaries';
  const params = [];
  if (filters.parentAccountId) params.push(`parent_account_id=${filters.parentAccountId}`);
  if (filters.parentAccountNumber) params.push(`parent_account_number=${filters.parentAccountNumber}`);
  if (filters.type) params.push(`type=${filters.type}`);
  if (filters.isActive !== undefined) params.push(`is_active=${filters.isActive}`);
  if (filters.search) params.push(`search=${encodeURIComponent(filters.search)}`);
  if (params.length) endpoint += `?${params.join('&')}`;
  
  const result = await apiCall(endpoint);
  return result.data || [];
};

export const getAuxiliary = async (id) => {
  const result = await apiCall(`/comptabilite/auxiliaries/${id}`);
  return result.data;
};

export const generateAuxiliaryCode = async (parentAccountNumber) => {
  const result = await apiCall(`/comptabilite/auxiliaries/generate-code/${parentAccountNumber}`);
  return result.data;
};

export const createAuxiliary = async (data) => {
  const now = new Date().toISOString();
  const payload = {
    code: data.code,
    parent_account_id: data.parentAccountId,
    parent_account_number: data.parentAccountNumber,
    prefix: data.prefix,
    name: data.name,
    name_ar: data.nameAr || '',
    type: data.type || 'other',
    address: data.address || '',
    phone: data.phone || '',
    email: data.email || '',
    tax_id: data.taxId || '',
    notes: data.notes || '',
    created_at: now,
    updated_at: now
  };
  const result = await apiCall('/comptabilite/auxiliaries', 'POST', payload);
  return result.data;
};

export const updateAuxiliary = async (id, data) => {
  const payload = { ...data, updated_at: new Date().toISOString() };
  const result = await apiCall(`/comptabilite/auxiliaries/${id}`, 'PUT', payload);
  return result.data;
};

export const deleteAuxiliary = async (id) => {
  const result = await apiCall(`/comptabilite/auxiliaries/${id}`, 'DELETE');
  return result.data;
};

export const getAuxiliariesSummary = async () => {
  const result = await apiCall('/comptabilite/auxiliaries/stats/summary');
  return result.data || {};
};

// =====================================================
// MULTI-CURRENCY
// =====================================================
export const getCurrencies = async () => {
  const result = await apiCall('/comptabilite/config/multi-currency/currencies');
  const rows = result.data || [];
  return rows.map((row) => normalizeCurrencyVolume({
    ...row,
    rate: Number(row.rate ?? 0),
    volume_sar: Number(row.volume_sar ?? row.volume_dzd ?? 0),
    is_base: Number(row.is_base ?? 0),
  }));
};

export const updateExchangeRates = async (rates) => {
  const result = await apiCall('/comptabilite/config/multi-currency/update-rates', 'POST', { rates });
  return result.data || [];
};

export const convertCurrency = async (amount, fromCurrency, toCurrency) => {
  const result = await apiCall('/comptabilite/config/multi-currency/convert', 'POST', {
    amount,
    from_currency: fromCurrency,
    to_currency: toCurrency
  });
  return result.data;
};

// =====================================================
// FISCAL YEARS
// =====================================================
export const getFiscalYears = async () => {
  const result = await apiCall('/comptabilite/config/fiscal-years');
  return result.data || [];
};

export const getCurrentFiscalYear = async () => {
  const result = await apiCall('/comptabilite/config/fiscal-years/current');
  return result.data;
};

export const createFiscalYear = async (data) => {
  const result = await apiCall('/comptabilite/config/fiscal-years', 'POST', data);
  return result.data;
};

export const closeFiscalYear = async (id, closedBy) => {
  const result = await apiCall(`/comptabilite/config/fiscal-years/${id}/close`, 'POST', { closed_by: closedBy });
  return result.data;
};

export const reopenFiscalYear = async (newYearId, previousYearId, openedBy) => {
  const result = await apiCall(`/comptabilite/config/fiscal-years/${newYearId}/reopen`, 'POST', {
    previous_year_id: previousYearId,
    opened_by: openedBy
  });
  return result.data;
};

// =====================================================
// LETTERING (Lettrage)
// =====================================================
export const autoMatchLettering = async (accountNumber, tolerance = 0.01) => {
  const result = await apiCall('/comptabilite/lettering/auto-match', 'POST', {
    account_number: accountNumber,
    tolerance: tolerance
  });
  return result.data;
};

export const applyLettering = async (entryLineIds, letteringCode = null, letteredBy = null) => {
  const result = await apiCall('/comptabilite/lettering/apply', 'POST', {
    entry_line_ids: entryLineIds,
    lettering_code: letteringCode,
    lettered_by: letteredBy
  });
  return result.data;
};

export const removeLettering = async (letteringCode, unletteredBy = null) => {
  const result = await apiCall('/comptabilite/lettering/unletter', 'POST', {
    lettering_code: letteringCode,
    unlettered_by: unletteredBy
  });
  return result.data;
};

export const getLetteringHistory = async (limit = 50, offset = 0) => {
  const result = await apiCall(`/comptabilite/lettering/history?limit=${limit}&offset=${offset}`);
  return result.data;
};

export const getLetteringStats = async () => {
  const result = await apiCall('/comptabilite/lettering/stats');
  return result.data || {};
};

// =====================================================
// LETTRAGE MANAGEMENT
// =====================================================
export const getUnletteredEntries = async (accountCode) => {
  const result = await apiCall(`/comptabilite/lettering/stats?account=${encodeURIComponent(accountCode || '')}`);
  return result.data || {};
};

export const applyAutoLettrage = async (accountCode, criteria = {}) => {
  const tolerance = parseFloat(criteria.tolerance) || 0.01;
  const letterPrefix = criteria.letterPrefix || 'L';

  const matchResult = await apiCall('/comptabilite/lettering/auto-match', 'POST', {
    account_number: accountCode,
    tolerance,
  });

  const matchedGroups = matchResult.data?.matched_groups || [];
  if (matchedGroups.length === 0) {
    return {
      matched_groups: 0,
      lettered_lines: 0,
      message: matchResult.data?.message || exportT('message_no_reconciliation'),
    };
  }

  let letteredLines = 0;
  const appliedCodes = [];

  for (let i = 0; i < matchedGroups.length; i++) {
    const group = matchedGroups[i];
    const lineIds = (group.lines || []).map((l) => l.id).filter(Boolean);
    if (lineIds.length === 0) continue;

    const letteringCode = `${letterPrefix}${String(i + 1).padStart(3, '0')}`;
    const applyResult = await apiCall('/comptabilite/lettering/apply', 'POST', {
      entry_line_ids: lineIds,
      lettering_code: letteringCode,
      lettered_by: criteria.lettered_by || 'system',
    });

    if (applyResult.success) {
      letteredLines += lineIds.length;
      appliedCodes.push(applyResult.data?.lettering_code || letteringCode);
    }
  }

  return {
    matched_groups: matchedGroups.length,
    lettered_lines: letteredLines,
    lettering_codes: appliedCodes,
    message: exportT('message_lettered_lines', {
      lettered: letteredLines,
      groups: matchedGroups.length,
    }),
  };
};

export const updateLineLettrage = async (entryId, lineIndex, lettrageCode) => {
  const result = await apiCall(`/comptabilite/entries/${entryId}/lines/${lineIndex}/lettrage`, 'PATCH', {
    lettrage: lettrageCode
  });
  return result.data;
};

// =====================================================
// TRANSFORM FUNCTIONS
// =====================================================
export const transformAccount = (data) => ({
  id: data.id,
  code: data.code,
  name: data.name,
  nameAr: data.name_ar,
  class: data.class,
  parentCode: data.parent_code,
  type: data.account_type,
  standard: data.standard,
  debitNature: data.debit_nature === 1,
  balance: data.balance,
  isActive: data.is_active === 1,
  color: data.color,
  notes: data.notes,
});

export const transformJournalEntry = (data) => ({
  id: data.id,
  date: data.entry_date || data.date,
  reference: data.reference,
  description: data.description,
  folio: data.folio,
  piece_number: data.piece_number,
  date_document: data.date_document,
  date_echeance: data.date_echeance,
  entry_number: data.entry_number,
  debit: {
    account: data.debit_account || '—',
    name: data.debit_account_name || '',
    amount: parseFloat(data.total_debit ?? data.debit_amount ?? 0),
  },
  credit: {
    account: data.credit_account || '—',
    name: data.credit_account_name || '',
    amount: parseFloat(data.total_credit ?? data.credit_amount ?? 0),
  },
  status: data.status,
  journalType: data.journal_type,
  documentRef: data.document_ref,
  documentType: data.document_type,
  period: data.period,
  fiscalYear: data.fiscal_year_id || data.fiscal_year,
  validatedBy: data.validated_by,
  validatedAt: data.validated_at,
  notes: data.notes,
  lines: data.lines,
});

export const transformTaxDeclaration = (data) => ({
  id: data.id,
  type: data.declaration_type,
  title: data.declaration_title,
  titleAr: data.declaration_title_ar,
  description: data.description,
  descriptionAr: data.description_ar,
  period: data.period,
  fiscalYear: data.fiscal_year,
  dueDate: data.due_date,
  status: data.status,
  totalBase: data.total_base,
  totalTax: data.total_tax,
  penalties: data.penalties,
  netAmount: data.net_amount,
  submittedAt: data.submitted_at,
  paidAt: data.paid_at,
  paymentReference: data.payment_reference,
  notes: data.notes,
});

export const transformMonthlyTracking = (data) => ({
  id: data.id,
  fiscalYear: data.fiscal_year,
  month: data.month,
  monthNumber: data.month_number,
  revenue: data.revenue,
  expenses: data.expenses,
  netResult: data.net_result,
  cashFlow: data.cash_flow,
  budgetRevenue: data.budget_revenue,
  budgetExpenses: data.budget_expenses,
  varianceRevenue: data.variance_revenue,
  varianceExpenses: data.variance_expenses,
  notes: data.notes,
});

// =====================================================
// ENTRY TEMPLATES
// =====================================================
export const getEntryTemplates = async (filters = {}) => {
  let endpoint = '/comptabilite/entry-templates';
  const params = [];
  if (filters.family) params.push(`family=${filters.family}`);
  if (filters.journal_type) params.push(`journal_type=${filters.journal_type}`);
  if (params.length) endpoint += `?${params.join('&')}`;
  const result = await apiCall(endpoint);
  return result.data || [];
};

export const createEntryTemplate = async (data) => {
  const result = await apiCall('/comptabilite/entry-templates', 'POST', data);
  return result.data;
};

export const generateEntryFromTemplate = async (templateId, variables) => {
  const result = await apiCall(`/comptabilite/entry-templates/${templateId}/generate`, 'POST', { variables });
  return result.data;
};

// =====================================================
// ENTRY VALIDATOR
// =====================================================
export const validateEntry = async (entryData, journalId) => {
  const result = await apiCall('/comptabilite/validator/validate-entry', 'POST', { entry: entryData, journal_id: journalId });
  return result.data;
};

export const getValidatorConfig = async (journalId) => {
  const result = await apiCall(`/comptabilite/validator/config/${journalId}`);
  return result.data;
};

export const updateValidatorConfig = async (journalId, config) => {
  const result = await apiCall(`/comptabilite/validator/config/${journalId}`, 'PUT', config);
  return result.data;
};

// =====================================================
// JOURNAL AUTHORIZATION
// =====================================================
export const getJournalAuthorizedAccounts = async (journalId) => {
  const result = await apiCall(`/comptabilite/journal/authorization/${journalId}`);
  return result.data || [];
};

export const addAuthorizedAccount = async (journalId, accountNumber, sense) => {
  const result = await apiCall(`/comptabilite/journal/authorization/${journalId}/accounts`, 'POST', { account_number: accountNumber, sense });
  return result.data;
};

export const removeAuthorizedAccount = async (journalId, accountNumber) => {
  const result = await apiCall(`/comptabilite/journal/authorization/${journalId}/accounts/${accountNumber}`, 'DELETE');
  return result.data;
};

// =====================================================
// GLOBAL SEARCH
// =====================================================
export const globalSearch = async (searchTerm, options = {}) => {
  const result = await apiCall('/comptabilite/config/search', 'POST', { search_term: searchTerm, ...options });
  return result.data;
};

export const getSearchHistory = async (limit = 10) => {
  const result = await apiCall(`/comptabilite/config/search/history?limit=${limit}`);
  return result.data || [];
};

// =====================================================
// REPORT EXPORT
// =====================================================
export const generateReportExport = async (reportType, format, filters = {}) => {
  const result = await apiCall('/comptabilite/analyses-rapports/report-export/generate', 'POST', { report_type: reportType, format, ...filters });
  return result.data;
};

// =====================================================
// BALANCE GÉNÉRALE (A03)
// =====================================================

/** Raw array or { data } from situation CRUD routes (comparatifs, cloture, rapports). */
const unwrapBalanceList = unwrapSituationList;

/** Extended tax-declaration routes return { declarations|declaration|details }. */
const unwrapExtendedTaxList = (result, key = 'declarations') => {
  if (Array.isArray(result)) return result;
  if (result?.data != null) return Array.isArray(result.data) ? result.data : [];
  if (Array.isArray(result?.[key])) return result[key];
  return [];
};

export const getBalances = async () => {
  const result = await apiCall('/comptabilite/balance');
  return unwrapBalanceList(result);
};

export const getBalance = async (id) => {
  return apiCall(`/comptabilite/balance/${id}`);
};

export const createBalance = async (payload) => {
  return apiCall('/comptabilite/balance', 'POST', payload);
};

export const updateBalance = async (id, payload) => {
  return apiCall(`/comptabilite/balance/${id}`, 'PUT', payload);
};

export const deleteBalance = async (id) => {
  return apiCall(`/comptabilite/balance/${id}`, 'DELETE');
};

// =====================================================
// COMPARATIFS & ÉVOLUTION (A12)
// =====================================================
export const getComparatifs = async () => {
  const result = await apiCall('/comptabilite/comparatifs');
  return unwrapSituationList(result);
};

export const getComparatif = async (id) => {
  return apiCall(`/comptabilite/comparatifs/${id}`);
};

export const createComparatif = async (data) => {
  return apiCall('/comptabilite/comparatifs', 'POST', data);
};

export const updateComparatif = async (id, data) => {
  return apiCall(`/comptabilite/comparatifs/${id}`, 'PUT', data);
};

export const deleteComparatif = async (id) => {
  return apiCall(`/comptabilite/comparatifs/${id}`, 'DELETE');
};

// =====================================================
// CLÔTURE COMPTABLE (A13)
// =====================================================
export const getClotures = async () => {
  const result = await apiCall('/comptabilite/cloture');
  return unwrapSituationList(result);
};

export const getCloture = async (id) => {
  return apiCall(`/comptabilite/cloture/${id}`);
};

export const createCloture = async (data) => {
  return apiCall('/comptabilite/cloture', 'POST', data);
};

export const updateCloture = async (id, data) => {
  return apiCall(`/comptabilite/cloture/${id}`, 'PUT', data);
};

export const deleteCloture = async (id) => {
  return apiCall(`/comptabilite/cloture/${id}`, 'DELETE');
};

export const verifyPeriodClosure = async (fiscalYearId, closureDate) => {
  const result = await apiCall('/comptabilite/cloture/verify', 'POST', {
    fiscal_year_id: fiscalYearId,
    closure_date: closureDate,
  });
  return result.data || result;
};

export const closePeriod = async (fiscalYearId, closureDate, userCode = 'system', force = false) => {
  const result = await apiCall('/comptabilite/cloture/close', 'POST', {
    fiscal_year_id: fiscalYearId,
    closure_date: closureDate,
    user_code: userCode,
    force,
  });
  return result.data || result;
};

export const reopenPeriod = async (fiscalYearId, userCode = 'system') => {
  const result = await apiCall('/comptabilite/cloture/reopen', 'POST', {
    fiscal_year_id: fiscalYearId,
    user_code: userCode,
  });
  return result.data || result;
};

// =====================================================
// BALANCE PREVIEW
// =====================================================
export const generateBalancePreview = async (filters = {}) => {
  const result = await apiCall('/comptabilite/balance/preview/generate', 'POST', filters);
  return result.data;
};

// =====================================================
// BUDGETS
// =====================================================
export const getAccountBudget = async (accountNumber, fiscalYearId = null) => {
  let endpoint = `/comptabilite/analyses-rapports/budgets/${accountNumber}`;
  if (fiscalYearId) endpoint += `?fiscal_year_id=${fiscalYearId}`;
  const result = await apiCall(endpoint);
  return result.data;
};

export const updateMonthlyBudget = async (budgetData) => {
  const result = await apiCall('/comptabilite/analyses-rapports/budgets', 'POST', budgetData);
  return result.data;
};

export const getBudgetComparison = async (accountNumber, fiscalYearId = null) => {
  let endpoint = `/comptabilite/analyses-rapports/budgets/comparison/${accountNumber}`;
  if (fiscalYearId) endpoint += `?fiscal_year_id=${fiscalYearId}`;
  const result = await apiCall(endpoint);
  return result.data;
};

// =====================================================
// FORMULAS
// =====================================================
export const getFormulas = async () => {
  const result = await apiCall('/comptabilite/config/formulas');
  return result.data || [];
};

export const createFormula = async (data) => {
  const result = await apiCall('/comptabilite/config/formulas', 'POST', data);
  return result.data;
};

export const evaluateFormula = async (formulaId, context, fiscalYearId) => {
  const result = await apiCall(`/comptabilite/config/formulas/${formulaId}/evaluate`, 'POST', { context, fiscal_year_id: fiscalYearId });
  return result.data;
};

// =====================================================
// USER PRIVILEGES
// =====================================================
export const getUserPrivileges = async (userId) => {
  const result = await apiCall(`/comptabilite/config/privileges/${userId}`);
  return result.data;
};

export const updateUserPrivileges = async (userId, privileges) => {
  const result = await apiCall(`/comptabilite/config/privileges/${userId}`, 'PUT', privileges);
  return result.data;
};

export const checkUserPermission = async (userId, permission) => {
  const result = await apiCall('/comptabilite/config/privileges/check', 'POST', { user_id: userId, permission });
  return result.data;
};

// =====================================================
// BACKUPS
// =====================================================
export const getBackupHistory = async (limit = 30) => {
  const result = await apiCall(`/comptabilite/config/backups/history?limit=${limit}`);
  return result.data || [];
};

export const createBackupSnapshot = async (snapshotType = 'manual', createdBy = null) => {
  const result = await apiCall('/comptabilite/config/backups/snapshot', 'POST', { snapshot_type: snapshotType, created_by: createdBy });
  return result.data;
};

export const exportFullData = async (format = 'json', fiscalYearId = null) => {
  const result = await apiCall('/comptabilite/config/backups/export-full', 'POST', { format, fiscal_year_id: fiscalYearId });
  return result.data;
};

// =====================================================
// PIECE RENUMBERING
// =====================================================
export const renumberPiecesByFolio = async (folio, digits = 6) => {
  const result = await apiCall('/comptabilite/renumber/by-folio', 'POST', { folio, digits });
  return result.data;
};

export const renumberPiecesByJournal = async (journalId, digits = 6, fiscalYearId = null) => {
  const result = await apiCall('/comptabilite/renumber/by-journal', 'POST', { journal_id: journalId, digits, fiscal_year_id: fiscalYearId });
  return result.data;
};

// ==========================================
// CODES MANAGEMENT (Codes Opérationnels)
// ==========================================

/**
 * Get all codes with optional filters
 */
export const getCodes = async (filters = {}) => {
  const queryParams = new URLSearchParams(filters).toString();
  const endpoint = `/comptabilite/codes${queryParams ? `?${queryParams}` : ''}`;
  const result = await apiCall(endpoint, 'GET');
  return result.data || [];
};

/**
 * Get a single code by ID
 */
export const getCode = async (id) => {
  const result = await apiCall(`/comptabilite/codes/${id}`, 'GET');
  return result.data || null;
};

/**
 * Create a new code
 */
export const createCode = async (data) => {
  const result = await apiCall('/comptabilite/codes', 'POST', data);
  return result;
};

/**
 * Update an existing code
 */
export const updateCode = async (id, data) => {
  const result = await apiCall(`/comptabilite/codes/${id}`, 'PUT', data);
  return result;
};

/**
 * Delete a code
 */
export const deleteCode = async (id) => {
  const result = await apiCall(`/comptabilite/codes/${id}`, 'DELETE');
  return result;
};

// ==========================================
// OPERATIONS MANAGEMENT
// ==========================================

/**
 * Get all operations with optional filters
 */
export const getOperations = async (filters = {}) => {
  const queryParams = new URLSearchParams(filters).toString();
  const endpoint = `/comptabilite/operations${queryParams ? `?${queryParams}` : ''}`;
  const result = await apiCall(endpoint, 'GET');
  return result.data || [];
};

/**
 * Get a single operation by ID
 */
export const getOperation = async (id) => {
  const result = await apiCall(`/comptabilite/operations/${id}`, 'GET');
  return result.data || null;
};

/**
 * Create a new operation
 */
export const createOperation = async (data) => {
  const result = await apiCall('/comptabilite/operations', 'POST', data);
  return result;
};

/**
 * Update an existing operation
 */
export const updateOperation = async (id, data) => {
  const result = await apiCall(`/comptabilite/operations/${id}`, 'PUT', data);
  return result;
};

/**
 * Delete an operation
 */
export const deleteOperation = async (id) => {
  const result = await apiCall(`/comptabilite/operations/${id}`, 'DELETE');
  return result;
};

// ==========================================
// AFFECTATIONS MANAGEMENT
// ==========================================

/**
 * Get all affectations with optional filters
 */
export const getAffectations = async (filters = {}) => {
  const queryParams = new URLSearchParams(filters).toString();
  const endpoint = `/comptabilite/affectations${queryParams ? `?${queryParams}` : ''}`;
  const result = await apiCall(endpoint, 'GET');
  return result.data || [];
};

/**
 * Get a single affectation by ID
 */
export const getAffectation = async (id) => {
  const result = await apiCall(`/comptabilite/affectations/${id}`, 'GET');
  return result.data || null;
};

/**
 * Create a new affectation
 */
export const createAffectation = async (data) => {
  const result = await apiCall('/comptabilite/affectations', 'POST', data);
  return result;
};

/**
 * Update an existing affectation
 */
export const updateAffectation = async (id, data) => {
  const result = await apiCall(`/comptabilite/affectations/${id}`, 'PUT', data);
  return result;
};

/**
 * Delete an affectation
 */
export const deleteAffectation = async (id) => {
  const result = await apiCall(`/comptabilite/affectations/${id}`, 'DELETE');
  return result;
};

// =====================================================
// TVCP (Tableau Variation Capitaux Propres)
// =====================================================
export const getTVCP = async (fiscalYearId) => {
  const result = await apiCall(`/comptabilite/tax-declarations/tvcp?fiscal_year_id=${fiscalYearId}`);
  return result.data || [];
};

export const generateTVCP = async (fiscalYearId) => {
  const result = await apiCall('/comptabilite/tax-declarations/tvcp/generate', 'POST', { fiscal_year_id: fiscalYearId });
  return result.data || [];
};

export const updateTVCPComponent = async (id, data) => {
  const result = await apiCall(`/comptabilite/tax-declarations/tvcp/${id}`, 'PUT', data);
  return result.data;
};

export const recalculateTVCP = async (fiscalYearId) => {
  const result = await apiCall('/comptabilite/tax-declarations/tvcp/calculate', 'POST', { fiscal_year_id: fiscalYearId });
  return result.data || [];
};

export const exportTVCP = async (fiscalYearId, format = 'excel') => {
  const result = await apiCall(`/comptabilite/tax-declarations/tvcp/export?fiscal_year_id=${fiscalYearId}&format=${format}`);
  return result.data;
};

export const getTVCPAuditLog = async (fiscalYearId) => {
  const result = await apiCall(`/comptabilite/tax-declarations/tvcp/audit?fiscal_year_id=${fiscalYearId}`);
  return result.data || [];
};

// =====================================================
// TAX DECLARATIONS EXTENDED (IBS, TAP, IRG)
// =====================================================
export const getIBSDeclarations = async (filters) => {
  let endpoint = '/comptabilite/tax-declarations/ibs';
  const params = [];
  if (filters) {
    if (filters.fiscalYear) params.push(`fiscalYear=${filters.fiscalYear}`);
    if (filters.status) params.push(`status=${filters.status}`);
  }
  if (params.length) endpoint += `?${params.join('&')}`;
  const result = await apiCall(endpoint);
  return unwrapExtendedTaxList(result, 'declarations');
};

export const getIBSDeclaration = async (id) => {
  const result = await apiCall(`/comptabilite/tax-declarations/ibs/${id}`);
  return result.data || result.declaration || result;
};

export const createIBSDeclaration = async (data) => {
  const result = await apiCall('/comptabilite/tax-declarations/ibs', 'POST', data);
  return result.data;
};

export const updateIBSDeclaration = async (id, data) => {
  const result = await apiCall(`/comptabilite/tax-declarations/ibs/${id}`, 'PUT', data);
  return result.data;
};

export const getTAPDeclarations = async (filters) => {
  let endpoint = '/comptabilite/tax-declarations/tap';
  const params = [];
  if (filters) {
    if (filters.year) params.push(`year=${filters.year}`);
    if (filters.period) params.push(`period=${filters.period}`);
  }
  if (params.length) endpoint += `?${params.join('&')}`;
  const result = await apiCall(endpoint);
  return unwrapExtendedTaxList(result, 'declarations');
};

export const createTAPDeclaration = async (data) => {
  const result = await apiCall('/comptabilite/tax-declarations/tap', 'POST', data);
  return result.data;
};

export const getIRGDeclarations = async (filters) => {
  let endpoint = '/comptabilite/tax-declarations/irg';
  const params = [];
  if (filters) {
    if (filters.year) params.push(`year=${filters.year}`);
    if (filters.period) params.push(`period=${filters.period}`);
    if (filters.category) params.push(`category=${filters.category}`);
  }
  if (params.length) endpoint += `?${params.join('&')}`;
  const result = await apiCall(endpoint);
  return unwrapExtendedTaxList(result, 'declarations');
};

export const createIRGDeclaration = async (data) => {
  const result = await apiCall('/comptabilite/tax-declarations/irg', 'POST', data);
  return result.data;
};

export const getIRGDetails = async (id, category) => {
  let endpoint = `/comptabilite/tax-declarations/irg/${id}/details`;
  if (category) endpoint += `?category=${category}`;
  const result = await apiCall(endpoint);
  return unwrapExtendedTaxList(result, 'details');
};

export default {
  // Dashboard
  getDashboardStats, getDashboardKPIs,
  // Chart of Accounts
  getChartOfAccounts, getAccount, createAccount, updateAccount, deleteAccount,
  getAnalyticalCodes, createAnalyticalCode, updateAnalyticalCode, deleteAnalyticalCode,
  getBalances, getBalance, createBalance, updateBalance, deleteBalance,
  getComparatifs, getComparatif, createComparatif, updateComparatif, deleteComparatif,
  getClotures, getCloture, createCloture, updateCloture, deleteCloture,
  verifyPeriodClosure, closePeriod, reopenPeriod,
  // Journal Entries
  getJournalEntries, getJournalEntry, createJournalEntry, updateJournalEntry, deleteJournalEntry, validateJournalEntry,
  // Financial Statements
  getBalanceSheet, createBalanceSheetItem, updateBalanceSheetItem,
  getIncomeStatement, createIncomeStatementItem,
  getCashFlowStatement, createCashFlowItem,
  getFluxTresorerie, createFluxTresorerie, updateFluxTresorerie, deleteFluxTresorerie,
  // Tax
  getTaxDeclarations, listTaxDeclarationRecords, getTaxDeclaration, createTaxDeclaration, updateTaxDeclaration, deleteTaxDeclaration,
  getTaxDeclarationItems, createTaxDeclarationItem,
  getTaxCalendar, createTaxCalendarEntry, updateTaxCalendarEntry,
  // Compliance
  getComplianceChecks, getComplianceOverview, getComplianceCheckCategories,
  getComplianceAuditLog, getComplianceBackupStatus, updateComplianceCheck,
  // Annexes & Liasse
  getAnnexeNotes, createAnnexeNote, updateAnnexeNote, deleteAnnexeNote,
  getRapportsPeriodiques, createRapportPeriodique, updateRapportPeriodique, deleteRapportPeriodique,
  getLiasseFiscale, createLiasseFiscaleItem, updateLiasseFiscaleItem,
  // Monthly & Stats
  getMonthlyTracking, createMonthlyTrackingEntry, updateMonthlyTrackingEntry,
  getAccountingStats, updateAccountingStats,
  // IFRS
  getIfrsMapping, getIfrsStandards, getIfrsDifferences, createIfrsMapping,
  getSpecializedSectorTypes, getSpecializedSectorData,
  getAdditionalTaxes, calculateAdditionalTax, recordAdditionalTax,
  // Assets
  getAssets, getAsset, createAsset, updateAsset, deleteAsset,
  // Multi-Currency
  getCurrencies, updateExchangeRates, convertCurrency,
  // Fiscal Years
  getFiscalYears, getCurrentFiscalYear, createFiscalYear, closeFiscalYear, reopenFiscalYear,
  // Auxiliaries
  getAuxiliaries, getAuxiliary, generateAuxiliaryCode, createAuxiliary, updateAuxiliary, deleteAuxiliary, getAuxiliariesSummary,
  // Lettering
  autoMatchLettering, applyLettering, removeLettering, getLetteringHistory, getLetteringStats,
  // Advanced Features
  getEntryTemplates, createEntryTemplate, generateEntryFromTemplate,
  validateEntry, getValidatorConfig, updateValidatorConfig,
  getJournalAuthorizedAccounts, addAuthorizedAccount, removeAuthorizedAccount,
  globalSearch, getSearchHistory,
  generateReportExport, generateBalancePreview,
  getAccountBudget, updateMonthlyBudget, getBudgetComparison,
  getFormulas, createFormula, evaluateFormula,
  getUserPrivileges, updateUserPrivileges, checkUserPermission,
  getBackupHistory, createBackupSnapshot, exportFullData,
  renumberPiecesByFolio, renumberPiecesByJournal,
  // Liasse Fiscale - Generation & Calculation
  generateLiasseFiscale, calculateAllTableaux, calculateSpecificTableau, exportLiasse,
  // Legal Compliance (Décret 09-110)
  getEventLog, getEventLogStats, logEvent,
  getVersions, addVersion,
  getParameterHistory, getParameterComparison,
  getBackupLog, logBackup,
  // Tax Declarations Extended
  getIBSDeclarations, getIBSDeclaration, createIBSDeclaration, updateIBSDeclaration,
  getTAPDeclarations, createTAPDeclaration,
  getIRGDeclarations, createIRGDeclaration, getIRGDetails,
  // TVCP
  getTVCP, generateTVCP, updateTVCPComponent, recalculateTVCP, exportTVCP, getTVCPAuditLog,
  // Codes Management
  getCodes, getCode, createCode, updateCode, deleteCode,
  // Operations Management
  getOperations, getOperation, createOperation, updateOperation, deleteOperation,
  // Affectations Management
  getAffectations, getAffectation, createAffectation, updateAffectation, deleteAffectation,
  // Transform
  transformAccount, transformJournalEntry, transformTaxDeclaration, transformComplianceCheck, transformMonthlyTracking,
};


